import { StatEventReport, StatPlatform } from '@/models/stats.ts'
import { useProfile } from '@/stores/useAuth.ts'
import { isIOSDevice } from '@/controller/pwaInstall.ts'

const ENDPOINT = `${import.meta.env.VITE_PUBLIC_API_URL ?? ''}/api/stats/`
const FLUSH_DELAY_MS = 800
/** The backend takes at most 50 events per request. */
const MAX_BATCH = 50

let queue: StatEventReport[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let disabled = false

/**
 * The B2B side (PicField portal) must not produce events. Set from
 * `Portal.page.tsx` while the portal is mounted.
 *
 * The server drops events whose logged-in user owns the content, but only when a
 * token is sent — so this is the belt to that suspenders, not a replacement.
 */
export function setStatTrackingDisabled(value: boolean) {
  disabled = value
}

/**
 * The portal renders the public gallery / portfolio inside a preview iframe.
 * That document is a fresh module instance, so `disabled` is false there — the
 * `_preview=1` marker on the previewed URL is what tells it apart.
 */
function isPreviewDocument(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('_preview') === '1'
  } catch {
    return false
  }
}

export function detectPlatform(): StatPlatform {
  if (/android/i.test(navigator.userAgent)) return 'android'
  if (isIOSDevice()) return 'ios'
  return 'web'
}

function send(events: StatEventReport[]) {
  if (!events.length) return

  const body = JSON.stringify(events.length === 1 ? events[0] : { events })
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  // Sent on purpose: it is what lets the server drop events created by the
  // owner of the content. `keepalive` (rather than sendBeacon) is what makes a
  // request with headers survive the page going away.
  const token = useProfile.getState().authenticationToken
  if (token) {
    headers['Authorization'] = `Token ${token}`
  }

  fetch(ENDPOINT, { method: 'POST', headers, body, keepalive: true }).catch(() => {})
}

function flush() {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  const events = queue
  queue = []
  send(events)
}

/**
 * Reports one event. Fire and forget — tracking never blocks and never throws.
 * Events are batched for a moment so a burst of clicks becomes one request.
 */
export function trackStat(event: StatEventReport) {
  if (disabled || isPreviewDocument()) return

  queue.push({ platform: detectPlatform(), ...event })
  if (queue.length >= MAX_BATCH) {
    flush()
    return
  }
  if (!timer) {
    timer = setTimeout(flush, FLUSH_DELAY_MS)
  }
}

// Leaving the page must not swallow what is still queued.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush()
    }
  })
}
